import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl, FormattedDate, FormattedMessage} from 'react-intl';
import {TransitionGroup, CSSTransition} from 'react-transition-group';

import {Icon} from '@stubhub/bamboo';
import {
  trimBothSides,
  SORT_ORDER,
  recapEventNamePieces,
  sortAncestorsByWords,
  transformAncestors,
} from '@stubhub/header-helpers';
import FormattedLink from '@stubhub/react-formatted-link';
import Logo, {BRAND_TYPES as LOGO_BRANDS, LOGO_TYPES} from '@stubhub/react-logo';
import SearchBar from '@stubhub/react-search-component';
import StoreConfigButton from '@stubhub/react-store-config-button';
import {connect} from '@stubhub/react-store-provider';
import Urgency from '@stubhub/react-urgency';

import getNavConfig from './nav-config';
import controller from './nav-controller';
import NavMenu from './nav-menu';
import messages from './nav-messages';

import '../styles/nav.scss';

/* istanbul ignore next*/
const NavItemGroup = ({
  id,
  items,
  activeItem,
  onItemClick,
  onOptionClick,
  onClickOutside,
  onMenuHover,
  onCategoryHover,
  selectedCurrency,
  enableCurrencySelector,
  selectedLanguage,
  enableLanguageSelector,
  screenSize,
}) => {
  return (
    <ul className={`mh__nav-item-group mh__nav-item-group__${id}`}>
      {(enableLanguageSelector || enableCurrencySelector) && screenSize !== 'small' && (
        <li className="mh__nav-item mh__nav-item-localization">
          <StoreConfigButton
            enableLanguageSelector={enableLanguageSelector}
            enableCurrencySelector={enableCurrencySelector}
            selectedCurrency={selectedCurrency}
            selectedLanguage={selectedLanguage}
          />
        </li>
      )}

      {items.map((menu) => {
        const href = menu.href || menu.webURI;

        return (
          <li className={`mh__nav-item mh__nav-item-${menu.id}`} key={menu.id}>
            <NavMenu
              className={menu.key}
              {...menu}
              href={href}
              onItemClick={onItemClick}
              onOptionClick={onOptionClick}
              onClickOutside={onClickOutside}
              onMenuHover={onMenuHover}
              onCategoryHover={onCategoryHover}
              active={menu.id === activeItem}
              activeMenuItem={activeItem}
              screenSize={screenSize}
            />
          </li>
        );
      })}
    </ul>
  );
};

export class HeaderNav extends React.Component {
  constructor(props, context) {
    super(props);

    const {deviceType} = props;
    const {globalRegistry} = context;
    const isMobile = deviceType === 'phone' || deviceType === 'tablet';
    const isEnabled =
      globalRegistry && typeof globalRegistry.getFeatureValue === 'function'
        ? globalRegistry.getFeatureValue.bind(globalRegistry)
        : () => undefined;

    this.exposeExpandedNavMenu = isEnabled('common.exposeExpandedNavMenu');
    this.debugSubCatNavUK = isEnabled('common.debugSubCatNavUK');
    this.enableBulletLogin = isEnabled('homepage.enableLogin');
    this.hasDiscoveryVipCategory = isEnabled('common.header.VipCategory');
    this.enableCurrencySelector = isEnabled('common.enableAdvisoryCurrency');
    this.locales = globalRegistry?.getSupporttedLocales();
    this.enableLanguageSelector = isEnabled('common.enableLanguageSelector') && this.locales.length > 1;
    this.selectedLanguage = globalRegistry?.getCurrentLocale()?.substr(0, 2);
    this.hasDiscoveryCitiesCategory = isMobile
      ? isEnabled('common.header.mwebCitiesCategory')
      : isEnabled('common.header.desktopCitiesCategory');
    this.useAltBrand = isEnabled('common.useAltBrand');
  }

  addItemAtPath(items, path = [], value = {}) {
    if (path?.length) {
      const [key] = path;
      path = path.slice(1);
      if (items && Array.isArray(items)) {
        items.forEach((item) => {
          if (item && item.id === key && item.items) {
            this.addItemAtPath(item.items, path, value);
          }
        });
      }
    } else {
      items.push(value);
    }
  }

  formatMessage(item, intl) {
    item.label = intl.formatMessage(messages[item.id]);
    if (messages[`${item.id}.toggle`]) {
      item.toggleLabel = intl.formatMessage(messages[`${item.id}.toggle`]);
    }
    if (item.items) {
      item.items.forEach((sub) => {
        this.formatMessage(sub, intl);
      });
    }
  }

  resetNavMenuState() {
    const {onNavMenuStateChange, setActiveMenuKey, activeMenuKey} = this.props;

    if (activeMenuKey !== null) {
      setActiveMenuKey(null);
    }

    onNavMenuStateChange(false);
  }

  updateNavMenuState(key) {
    const {activeMenuKey, setActiveMenuKey, onNavMenuStateChange} = this.props;

    if (!activeMenuKey || activeMenuKey !== key) {
      /*
       * No active key currently, or the current active key is different,
       * need to make new key active
       */
      setActiveMenuKey(key);

      onNavMenuStateChange(true);
    } else {
      // The key is currently active, make it inactive
      this.resetNavMenuState();
    }
  }

  clearUserAuth = () => {
    if (typeof window !== 'undefined' && window.location) {
      const domain = window.location.host.split('www').pop();

      const {signOutUser} = this.props;

      signOutUser(domain)
        .then(() => {
          // Console logs showed that this line is executing, but coverage may have issue with the promise
          /* istanbul ignore next */
          this.resetNavMenuState();

          if (/^\/my\//i.test(window.location.pathname)) {
            window.location.assign('/');
          }

          return '';
        })
        .catch({});
    }
  };

  onItemClick = (key, items) => {
    if (key === 'search') {
      /**
       * Handle Search Button Click
       *
       */
      const {showSearchBarModal} = this.props;
      showSearchBarModal && /* istanbul ignore next */ showSearchBarModal(); // Ignore Defensive Coding for `showSearchBarModal` Function.
      this.resetNavMenuState();
    } else if (items) {
      // In these cases, there are nav menu items, we need to show/hide them
      this.updateNavMenuState(key, items);
    }
  };

  onOptionClick = (key) => {
    const {screenSize} = this.props;
    if (key === 'logout') {
      this.clearUserAuth();
    }

    // Without this check, the mweb sell dropdown menu resets the nav state before it can expand
    if (key === 'sell' && screenSize === 'small') {
      return;
    }

    // Doesn't matter what option was clicked, reset nav menu state
    this.resetNavMenuState();
  };

  onClickOutside = () => {
    // Reset nav menu state
    this.resetNavMenuState();
  };

  onMenuHover = (key, items) => {
    const {activeMenuKey, setActiveMenuKey} = this.props;

    /*
     * The sell & profile keys are to enable the dropdown on hover in desktop. Removing it still allows them to open
     * in desktop but makes the mobile menu fail, immediately closing after being opened
     */
    if (!activeMenuKey && ['sell', 'profile'].includes(key) && items) {
      setActiveMenuKey(key);
    }
  };

  renderEventName(performers, groupings, eventName) {
    // Order performers by names wth greatest number of words in the name string.
    const transformedPerformers = transformAncestors(performers);
    const sortedPerformers = sortAncestorsByWords(transformedPerformers, SORT_ORDER.DESCENDING);

    // Order groupings by names wth lowest number of words in the name string.
    const transformedGroupings = transformAncestors(groupings);
    const sortedGroupings = sortAncestorsByWords(transformedGroupings, SORT_ORDER.ASCENDING);

    const cleanPerformerNames = trimBothSides(sortedPerformers.map((element) => element.cleanName)).filter(
      (element) => element !== '' && element !== ' '
    );
    const cleanGroupingNames = trimBothSides(sortedGroupings.map((element) => element.cleanName)).filter(
      (element) => element !== '' && element !== ' '
    );

    // Create a map of the starting locations within the event name to the performer/grouping data.
    const eventNamePieces = {};
    // Iterate through all of the performers and groupings for which we want to link.
    recapEventNamePieces(eventName, eventNamePieces, sortedGroupings, cleanGroupingNames);
    recapEventNamePieces(eventName, eventNamePieces, sortedPerformers, cleanPerformerNames);

    // Iterate through all of the pieces of the event name that are performers or groupings for which we want to link.
    const elements = [];
    const eventNamePiecesKeys = Object.keys(eventNamePieces);
    eventNamePiecesKeys.forEach((pieceIndex, index) => {
      const startIndex =
        index === 0
          ? 0
          : parseInt(eventNamePiecesKeys[index - 1]) + eventNamePieces[eventNamePiecesKeys[index - 1]].cleanName.length;

      // Add a span for the piece of the event name preceding the performer/grouping link.
      elements.push(<span key={`span-${pieceIndex}`}>{eventName.substring(startIndex, pieceIndex)}</span>);

      // Add a link for the performer/grouping.
      elements.push(
        <FormattedLink key={`link-${pieceIndex}`} href={eventNamePieces[pieceIndex].webURI}>
          {eventNamePieces[pieceIndex].cleanName}
        </FormattedLink>
      );
    });

    // Add a span for the rest of the event name once we've exhausted all of the performer/grouping link pieces.
    const startIndex =
      eventNamePiecesKeys.length === 0
        ? 0
        : parseInt(eventNamePiecesKeys[eventNamePiecesKeys.length - 1]) +
          eventNamePieces[eventNamePiecesKeys[eventNamePiecesKeys.length - 1]].cleanName.length;
    elements.push(<span key="last-span">{eventName.substring(startIndex)}</span>);

    return elements;
  }

  render() {
    const {
      intl,
      onModalSearchActiveChanged,
      screenSize,
      onlyNavSecondary,
      deviceType,
      multiLayeredMenu,
      selectedCurrency,
      activeMenuKey,
      eventData,
      showImportantInfoIcon,
      handleImportantIconClick,
      subCategories,
      searchBarStyle,
      isModalSearchActive,
      addTitleKeywords,
    } = this.props;
    const {globalRegistry} = this.context;
    const isSessionAuthEnabled = globalRegistry.getFeatureValue('common.sessionAuthEnabled');
    const storeId = this.debugSubCatNavUK ? 2 : globalRegistry.getStoreId(); // OJO Debug only!!
    const locale = globalRegistry.getCurrentLocale();
    const enableMultiLayeredMenu = deviceType === 'phone' && multiLayeredMenu && this.exposeExpandedNavMenu;

    const data = getNavConfig(
      storeId,
      locale,
      this.enableBulletLogin,
      this.hasDiscoveryVipCategory,
      this.hasDiscoveryCitiesCategory,
      enableMultiLayeredMenu,
      isSessionAuthEnabled
    );
    this.configData = data;

    const {
      eventName,
      eventDate,
      eventDateLocal,
      eventTime,
      venueName,
      locality,
      state,
      allPerformers,
      groupings,
      venueWebURI,
      buyerMessages,
      totalTickets,
      onSaleDate,
    } = eventData || '';

    const {actions} = data;
    const {discovery} = data;
    if (this.exposeExpandedNavMenu && multiLayeredMenu?.discovery) {
      const {discovery: multiLayeredDiscovery} = multiLayeredMenu;
      // Safe check for multiLayeredDiscovery
      multiLayeredDiscovery.forEach((item) => {
        const index = discovery.findIndex((discoveryItem) => discoveryItem.id === item.id);
        if (index !== -1) {
          discovery[index] = item;
        }
      });
      // discovery = multiLayeredDiscovery;
      this.configData.discovery = discovery;
    }

    /**
     * Add search button, if needed, to appropriate menu
     *
     */
    const searchNavItem = {
      id: 'search',
      type: 'menu',
    };

    if (searchBarStyle === 'modal') {
      // Need to add search button
      if (screenSize === 'small') {
        actions.unshift(searchNavItem);
      }
    }

    if (this.hasDiscoveryCitiesCategory && subCategories?.cities) {
      this.addItemAtPath(actions, ['menu', 'menu-category'], {id: 'cities', type: 'menu', items: []});
    }

    if (this.hasDiscoveryVipCategory && subCategories?.vip) {
      this.addItemAtPath(actions, ['menu', 'menu-category'], {id: 'vip', type: 'menu', items: []});
    }

    // Add labels
    for (const key in data) {
      const group = data[key];
      group.forEach((item) => {
        this.formatMessage(item, intl);
      });
    }

    if (this.hasDiscoveryCitiesCategory && subCategories?.cities) {
      subCategories.cities.forEach((item, index) => {
        if (item?.name && item.webURI) {
          this.addItemAtPath(actions, ['menu', 'menu-category', 'cities'], {
            id: index,
            type: 'link',
            label: item.name,
            href: item.webURI,
          });
        }
      });
    }

    if (this.hasDiscoveryVipCategory && subCategories?.vip) {
      subCategories.vip.forEach((item, index) => {
        if (item?.name && item.webURI) {
          this.addItemAtPath(actions, ['menu', 'menu-category', 'vip'], {
            id: index,
            type: 'link',
            label: item.name,
            href: item.webURI,
          });
        }
      });
    }

    let navCls = 'mh__nav-top';
    if (isModalSearchActive) {
      navCls += ' mh__nav--searchActive';
    }

    const eventImportantIcon = (
      <span className="important-icon" onClick={handleImportantIconClick}>
        <Icon size="sm" name="Info" data-testid="important-icon" />
      </span>
    );

    const headerContent = eventName ? this.renderEventName(allPerformers, groupings, eventName) : null;

    const eventDetails = (
      <div className="mh__nav-event-details">
        <div className="event-heading">
          {eventName && (
            <h1 className="event-title">
              {addTitleKeywords ? (
                <FormattedMessage
                  id="event.header.title"
                  defaultMessage="{content} Tickets"
                  values={{content: headerContent}}
                />
              ) : (
                headerContent
              )}
            </h1>
          )}
          {showImportantInfoIcon && buyerMessages && buyerMessages.length > 0 && eventImportantIcon}
        </div>
        <div className="event-info">
          <span>
            {eventDate ? (
              <FormattedDate weekday="short" value={eventDate} month="short" day="2-digit" year="numeric" />
            ) : (
              <FormattedMessage defaultMessage="TBD" id="EventDetailInfo.TBD" />
            )}
          </span>
          &nbsp;•&nbsp;
          {eventTime && <time>{eventTime}</time>}
          {venueName && (
            <span>
              &nbsp;
              <FormattedMessage defaultMessage="at" id="EventDetailInfo.at" />
              &nbsp;
              <FormattedLink href={venueWebURI}>{venueName}</FormattedLink>,&nbsp;
              {locality}, {state}
            </span>
          )}
        </div>
        <Urgency eventDateLocal={eventDateLocal} totalTickets={totalTickets} onSaleDate={onSaleDate} />
      </div>
    );

    const navSearch = !onlyNavSecondary ? (
      <div className="mh__nav-search">
        <TransitionGroup>
          <CSSTransition classNames="HeaderSearchBar" timeout={{enter: 500, exit: 500}}>
            <SearchBar
              searchStyle={searchBarStyle}
              onModalSearchActiveChanged={onModalSearchActiveChanged}
              suggestVariant={7}
              merchClassName="EventHeader"
            />
          </CSSTransition>
        </TransitionGroup>
      </div>
    ) : null;

    const logoType = LOGO_TYPES.MERCH;
    const logoBrand = this.useAltBrand ? LOGO_BRANDS.ALTERNATIVE : LOGO_BRANDS.DEFAULT;

    const navTop = !onlyNavSecondary ? (
      <nav className={navCls}>
        <div className="mh__nav-primary">
          <div className="mh__nav-left-section">
            <Logo type={logoType} onItemClick={this.onItemClick} brand={logoBrand} />
            <div className="details-divider" />
            {eventDetails}
          </div>
          <div className="mh__nav-right-section">
            {navSearch}
            {!isModalSearchActive && (
              <NavItemGroup
                id="actions"
                items={actions}
                activeItem={activeMenuKey}
                onItemClick={this.onItemClick}
                onOptionClick={this.onOptionClick}
                onClickOutside={this.onClickOutside}
                onMenuHover={this.onMenuHover}
                screenSize={screenSize}
                selectedCurrency={selectedCurrency}
                enableCurrencySelector={this.enableCurrencySelector}
                selectedLanguage={this.selectedLanguage}
                enableLanguageSelector={this.enableLanguageSelector}
              />
            )}
          </div>
        </div>
      </nav>
    ) : null;

    return (
      <div className="mh__nav-container">
        <div className="mh__nav-wrapper">{navTop}</div>
      </div>
    );
  }
}

HeaderNav.propTypes = {
  /** function to show search bar modal */
  showSearchBarModal: PropTypes.func,

  /** search bar style */
  searchBarStyle: PropTypes.string,

  /** if the search bar modal is active */
  isModalSearchActive: PropTypes.bool,

  /** screen size */
  screenSize: PropTypes.string,

  /** callback when the search modal state has changed */
  onModalSearchActiveChanged: PropTypes.func,

  /** active menu key */
  activeMenuKey: PropTypes.string,

  /** wheter to show the important info icon */
  showImportantInfoIcon: PropTypes.string,

  /** callback when the important info icon is clicked */
  handleImportantIconClick: PropTypes.func,

  /** device type */
  deviceType: PropTypes.string,

  /** multi layered menu data */
  multiLayeredMenu: PropTypes.object,

  /** event data */
  eventData: PropTypes.object,

  /** sub categories */
  subCategories: PropTypes.object,

  /** if the title keywords should be added */
  addTitleKeywords: PropTypes.bool,

  /** function to set the active menu key */
  setActiveMenuKey: PropTypes.func,

  /** function to sign out user */
  signOutUser: PropTypes.func,

  /** callback when the menu state changes */
  onNavMenuStateChange: PropTypes.func,

  /** intl object */
  intl: PropTypes.object,

  /** only show the secondary nav */
  onlyNavSecondary: PropTypes.bool,

  /** selected currency code*/
  selectedCurrency: PropTypes.string,
};

HeaderNav.contextTypes = {
  globalRegistry: PropTypes.object,
  cookies: PropTypes.object,
};

HeaderNav.defaultProps = {
  searchBarStyle: 'expanded',
  isModalSearchActive: false,
  screenSize: 'large',
  activeMenuKey: null,
  showImportantInfoIcon: false,
};

NavItemGroup.propTypes = {
  /** id of the group */
  id: PropTypes.string,

  /** items in the group */
  items: PropTypes.array,

  /** active item */
  activeItem: PropTypes.string,

  /** callback when an item is clicked */
  onItemClick: PropTypes.func,

  /** callback when an option is clicked */
  onOptionClick: PropTypes.func,

  /** callback when a click is detected outside of the component */
  onClickOutside: PropTypes.func,

  /** callback when a menu is hovered */
  onMenuHover: PropTypes.func,

  /** callback when a category is hovered */
  onCategoryHover: PropTypes.func,

  /** selected currency */
  selectedCurrency: PropTypes.string,

  /** wheter the currency selector is enabled */
  enableCurrencySelector: PropTypes.bool,

  /** selected language */
  selectedLanguage: PropTypes.string,

  /** wheter the language selector is enabled */
  enableLanguageSelector: PropTypes.bool,

  /** screen size */
  screenSize: PropTypes.string,
};

export default connect(controller)(injectIntl(HeaderNav));
